import type { TutorialSlide } from '../screens/TutorialScreen'
import { NeonColors, FONTS, TOP5_COLORS } from '../neon-theme'
import { HEADING_FONT, TITLE_FONT_SIZE } from './tutorialStyles'
import { drawRightFinger, drawLeftFinger } from './fingerHelper'

const SCALE = 2
const MAP_SIZE = 242
const MAP_RADIUS = MAP_SIZE / 2
const TICK_COUNT = 60

// Timeline (3s per phase):
// 0–3:    "You" highlight
// 3–6:    "Portal" highlight
// 6–9:    "Richest snakes" highlight
// 9–12:   "Other snakes" highlight
// 12–15:  "Drag to move"
// 15–18:  "Pinch to resize"
// 18–21:  "Rounds last 60 minutes" — all 60 ticks pulse
// 21–24:  "Minutes remaining" — 20 burned, 40 bright
// 24–27:  "Current minute progress" — burn point highlighted
// 27–30:  "Evacuate when time runs out" — last minute red pulse
// 30–33:  "Otherwise..." — map explodes
// 33–34.5: fade out
// 34.5–36: fade in
const LOOP_DURATION = 36

type Phase = 'player' | 'portal' | 'top' | 'others' | 'drag' | 'pinch'
  | 'round' | 'remaining' | 'current' | 'lastmin' | 'explode'

export class MinimapSlide implements TutorialSlide {
  private container: HTMLDivElement | null = null
  private canvas: HTMLCanvasElement | null = null
  private ctx: CanvasRenderingContext2D | null = null
  private canvasW = 0; private canvasH = 0
  private animFrame = 0; private startTime = 0
  private mounted = false

  // Explosion particles (spawned once at t=30)
  private explParts: Array<{ angle: number; speed: number; r: number; color: string; decay: number }> = []
  private explSpawned = false

  private player = { x: 10, z: 15, score: 234 }
  private portal = { x: -45, z: -30 }
  private topSnakes = [
    { x: 55, z: -20, score: 445, color: TOP5_COLORS[0]! },
    { x: -30, z: 50, score: 312, color: TOP5_COLORS[1]! },
    { x: 40, z: 40, score: 289, color: TOP5_COLORS[2]! },
  ]
  private otherSnakes = [
    { x: -60, z: -55 }, { x: 70, z: 10 }, { x: -20, z: -70 },
    { x: 50, z: -60 }, { x: -50, z: 30 }, { x: 10, z: -40 },
  ]

  mount(el: HTMLDivElement, _onReady: () => void): void {
    this.container = el
    el.style.padding = '0'

    this.canvas = document.createElement('canvas')
    this.canvas.style.cssText = 'position: absolute; inset: 0; width: 100%; height: 100%;'
    el.appendChild(this.canvas)

    this.mounted = true; this.canvasW = 0; this.canvasH = 0; this.animFrame = 0
    this.startTime = performance.now()
    this.explSpawned = false; this.explParts = []
    this.tick()
  }

  unmount(): void {
    this.mounted = false
    if (this.animFrame) cancelAnimationFrame(this.animFrame)
    this.animFrame = 0
    if (this.container) this.container.innerHTML = ''
    this.container = null; this.canvas = null; this.ctx = null
  }

  private resizeCanvas(): void {
    if (!this.canvas) return
    const rect = this.canvas.getBoundingClientRect()
    const w = Math.floor(rect.width) || 320; const h = Math.floor(rect.height) || 500
    if (w === this.canvasW && h === this.canvasH) return
    this.canvasW = w; this.canvasH = h
    this.canvas.width = w * SCALE; this.canvas.height = h * SCALE
    this.ctx = this.canvas.getContext('2d')
  }

  private tick = (): void => {
    if (!this.mounted || !this.canvas) return
    this.resizeCanvas()
    if (!this.ctx || this.canvasW === 0) { this.animFrame = requestAnimationFrame(this.tick); return }
    const elapsed = (performance.now() - this.startTime) / 1000
    const t = elapsed % LOOP_DURATION
    // Reset explosion on loop
    if (t < 0.1 && elapsed > 1) { this.explSpawned = false; this.explParts = [] }
    this.draw(t)
    this.animFrame = requestAnimationFrame(this.tick)
  }

  private getPhase(t: number): Phase {
    if (t < 3) return 'player'
    if (t < 6) return 'portal'
    if (t < 9) return 'top'
    if (t < 12) return 'others'
    if (t < 15) return 'drag'
    if (t < 18) return 'pinch'
    if (t < 21) return 'round'
    if (t < 24) return 'remaining'
    if (t < 27) return 'current'
    if (t < 30) return 'lastmin'
    return 'explode'
  }

  private draw(t: number): void {
    const ctx = this.ctx!
    const w = this.canvasW * SCALE
    const h = this.canvasH * SCALE
    const s = SCALE

    // Fade: out 33–34.5, in 34.5–36
    const gf = t >= 33 && t < 34.5 ? Math.max(0, 1 - (t - 33) / 1.5)
      : t >= 34.5 ? Math.min(1, (t - 34.5) / 1.5)
        : 1

    // During fade-in (34.5–36), draw content as if t≈0 (player phase)
    // so the viewer sees the first scene fading in, not "Otherwise..."
    const ct = t >= 34.5 ? t - LOOP_DURATION : t

    ctx.clearRect(0, 0, w, h)
    ctx.fillStyle = NeonColors.bgDark; ctx.fillRect(0, 0, w, h)

    // Title
    ctx.textAlign = 'center'; ctx.textBaseline = 'top'
    ctx.font = `bold ${TITLE_FONT_SIZE}px ${FONTS.title}`
    ctx.fillStyle = NeonColors.primary; ctx.shadowColor = NeonColors.primaryGlow; ctx.shadowBlur = 12 * s
    ctx.globalAlpha = 0.95 * gf; ctx.fillText('MINIMAP', w / 2, 53 * s)
    ctx.shadowBlur = 0; ctx.globalAlpha = 1

    const phase = this.getPhase(ct)
    const dimAlpha = 0.25; const brightAlpha = 1.0

    // Map positioning
    let mapCX = w / 2; let mapCZ = h * 0.42
    let mapScale = 1.0

    if (phase === 'drag') {
      const dragT = (ct - 12) / 3
      mapCX += Math.sin(dragT * Math.PI * 2) * 30 * s
      mapCZ += Math.cos(dragT * Math.PI * 2) * 20 * s
    } else if (phase === 'pinch') {
      const pinchT = (ct - 15) / 3
      mapScale = 1 + Math.sin(pinchT * Math.PI) * 0.3
    }

    const mr = MAP_RADIUS * s * mapScale

    // Explosion phase: map shrinks and particles fly
    const isExplode = phase === 'explode'
    let explodeT = 0
    if (isExplode) {
      explodeT = (ct - 30) / 3 // 0→1
      // Spawn particles once
      if (!this.explSpawned) {
        this.explSpawned = true
        this.explParts = []
        const colors = ['#FF4444', '#FF6622', '#FFAA00', NeonColors.gold, '#FF2222', '#DDDDEE']
        for (let i = 0; i < 120; i++) {
          const angle = Math.random() * Math.PI * 2
          this.explParts.push({
            angle,
            speed: 80 + Math.random() * 250,
            r: 1.5 + Math.random() * 3,
            color: colors[Math.floor(Math.random() * colors.length)]!,
            decay: 0.6 + Math.random() * 0.4,
          })
        }
      }
    }

    // Don't draw map during late explosion (after shrink completes)
    const mapVisible = !isExplode || explodeT < 0.3
    const mapShrink = isExplode ? Math.max(0, 1 - explodeT * 4) : 1

    if (mapVisible && gf > 0.01) {
      const emr = mr * mapShrink

      // Map background
      ctx.save()
      ctx.beginPath()
      ctx.arc(mapCX, mapCZ, emr, 0, Math.PI * 2)
      ctx.fillStyle = 'rgba(0, 2, 10, 0.8)'
      ctx.globalAlpha = gf; ctx.fill()

      // Border: red during lastmin, white otherwise
      if (phase === 'lastmin') {
        const dangerPulse = 0.4 + Math.sin(Date.now() * 0.008) * 0.6
        ctx.strokeStyle = NeonColors.danger; ctx.globalAlpha = dangerPulse * gf
      } else {
        ctx.strokeStyle = 'rgba(255,255,255,0.2)'; ctx.globalAlpha = gf
      }
      ctx.lineWidth = 1.5 * s; ctx.stroke()
      ctx.clip()

      // Grid
      ctx.strokeStyle = 'rgba(255,255,255,0.06)'; ctx.lineWidth = 1
      const gridStep = 20 * s * mapScale
      for (let gx = mapCX - emr; gx < mapCX + emr; gx += gridStep) {
        ctx.beginPath(); ctx.moveTo(gx, mapCZ - emr); ctx.lineTo(gx, mapCZ + emr); ctx.stroke()
      }
      for (let gz = mapCZ - emr; gz < mapCZ + emr; gz += gridStep) {
        ctx.beginPath(); ctx.moveTo(mapCX - emr, gz); ctx.lineTo(mapCX + emr, gz); ctx.stroke()
      }

      // Other snakes
      const otherA = phase === 'others' ? brightAlpha : dimAlpha
      for (const sn of this.otherSnakes) {
        ctx.beginPath()
        ctx.arc(mapCX + sn.x * s * mapScale * mapShrink, mapCZ + sn.z * s * mapScale * mapShrink, 2.5 * s, 0, Math.PI * 2)
        ctx.fillStyle = NeonColors.textBright; ctx.globalAlpha = otherA * 0.35 * gf; ctx.fill()
      }
      ctx.globalAlpha = 1

      // Top snakes
      const topA = phase === 'top' ? brightAlpha : dimAlpha
      ctx.font = `bold ${9 * s}px Inter, sans-serif`
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
      for (const sn of this.topSnakes) {
        ctx.globalAlpha = topA * gf; ctx.fillStyle = sn.color
        ctx.shadowColor = sn.color; ctx.shadowBlur = phase === 'top' ? 8 * s : 0
        ctx.fillText(String(sn.score), mapCX + sn.x * s * mapScale * mapShrink, mapCZ + sn.z * s * mapScale * mapShrink)
      }
      ctx.shadowBlur = 0; ctx.globalAlpha = 1

      // Portal
      const portalA = phase === 'portal' ? brightAlpha : dimAlpha
      const portalTime = performance.now() * 0.003
      const breathe = Math.sin(portalTime * 1.5) * 0.5 + 0.5
      const px = mapCX + this.portal.x * s * mapScale * mapShrink
      const pz = mapCZ + this.portal.z * s * mapScale * mapShrink

      ctx.globalAlpha = portalA * (0.08 + breathe * 0.06) * gf
      ctx.beginPath(); ctx.arc(px, pz, (9 + breathe * 2) * s * mapScale * mapShrink, 0, Math.PI * 2)
      ctx.fillStyle = NeonColors.portal; ctx.fill()

      for (let arm = 0; arm < 3; arm++) {
        const armOff = (arm * Math.PI * 2) / 3
        ctx.beginPath()
        for (let j = 0; j <= 16; j++) {
          const frac = j / 16; const r = 8 * (1 - frac * 0.85) * s * mapScale * mapShrink
          const angle = armOff + portalTime * 2 + frac * Math.PI * 2.5
          const sx = px + Math.cos(angle) * r, sz = pz + Math.sin(angle) * r
          if (j === 0) ctx.moveTo(sx, sz); else ctx.lineTo(sx, sz)
        }
        ctx.strokeStyle = NeonColors.portal; ctx.globalAlpha = portalA * (0.5 + breathe * 0.3) * gf
        ctx.lineWidth = (1.5 - breathe * 0.3) * s; ctx.stroke()
      }
      ctx.globalAlpha = 1

      // Player
      const playerA = phase === 'player' ? brightAlpha : dimAlpha
      const pulse = 0.85 + Math.sin(performance.now() * 0.003) * 0.15
      ctx.font = `bold ${10 * s}px Inter, sans-serif`
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
      ctx.globalAlpha = playerA * pulse * gf; ctx.fillStyle = '#FFFFFF'
      ctx.shadowColor = '#FFFFFF'; ctx.shadowBlur = phase === 'player' ? 12 * s : 0
      ctx.fillText(String(this.player.score), mapCX + this.player.x * s * mapScale * mapShrink, mapCZ + this.player.z * s * mapScale * mapShrink)
      ctx.shadowBlur = 0; ctx.globalAlpha = 1

      ctx.restore()

      // Tick ring (outside map, not clipped)
      if (!isExplode) {
        this.drawTickRing(ctx, mapCX, mapCZ, mr, ct, phase)
      }
    }

    // Explosion particles
    if (isExplode && this.explParts.length > 0) {
      for (const p of this.explParts) {
        const dist = p.speed * explodeT * s
        const px = mapCX + Math.cos(p.angle) * dist
        const pz = mapCZ + Math.sin(p.angle) * dist
        const alpha = Math.max(0, p.decay - explodeT * 0.8)
        if (alpha < 0.01) continue
        ctx.beginPath(); ctx.arc(px, pz, p.r * s, 0, Math.PI * 2)
        ctx.fillStyle = p.color; ctx.globalAlpha = alpha * gf
        ctx.shadowColor = p.color; ctx.shadowBlur = p.r * s
        ctx.fill(); ctx.shadowBlur = 0
      }
      ctx.globalAlpha = 1
    }

    // Explosion flash
    if (isExplode && explodeT < 0.15) {
      ctx.fillStyle = '#FFFFFF'
      ctx.globalAlpha = (1 - explodeT / 0.15) * 0.4 * gf
      ctx.fillRect(0, 0, w, h)
      ctx.globalAlpha = 1
    }

    // Subtitle text
    const titleBottom = 77 * s
    const mapTopBase = h * 0.42 - MAP_RADIUS * s
    const textZ = (titleBottom + mapTopBase) / 2
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
    ctx.font = HEADING_FONT; ctx.fillStyle = NeonColors.textBright
    ctx.globalAlpha = 0.9 * gf

    switch (phase) {
      case 'player': ctx.fillText('You and your current sparks', w / 2, textZ); break
      case 'portal': ctx.fillText('Portal — your way out', w / 2, textZ); break
      case 'top': ctx.fillText('Richest snakes on the map', w / 2, textZ); break
      case 'others': ctx.fillText('Other snakes', w / 2, textZ); break
      case 'drag': ctx.fillText('Drag to move', w / 2, textZ); break
      case 'pinch': ctx.fillText('Pinch to resize', w / 2, textZ); break
      case 'round': ctx.fillText('Rounds last 60 minutes', w / 2, textZ); break
      case 'remaining': ctx.fillText('Minutes remaining', w / 2, textZ); break
      case 'current': ctx.fillText('Current minute progress', w / 2, textZ); break
      case 'lastmin':
        ctx.fillStyle = NeonColors.danger
        ctx.fillText('Evacuate when time runs out', w / 2, textZ)
        break
      case 'explode':
        ctx.fillStyle = NeonColors.danger
        ctx.fillText('Otherwise...', w / 2, textZ)
        break
    }
    ctx.globalAlpha = 1

    // Fingers
    if (phase === 'drag') {
      const dragT = (ct - 12) / 3
      const fx = mapCX + Math.sin(dragT * Math.PI * 2) * 30 * s
      const fz = mapCZ + Math.cos(dragT * Math.PI * 2) * 20 * s + 40 * s
      drawRightFinger(ctx, fx, fz, 36 * s, 0.6 * gf)
    }

    if (phase === 'pinch') {
      const pinchT = (ct - 15) / 3
      const spread = 30 * s * mapScale + (mapScale - 1) * 80 * s
      drawLeftFinger(ctx, mapCX - spread, mapCZ + 30 * s, 32 * s, 0.6 * gf)
      drawRightFinger(ctx, mapCX + spread, mapCZ + 30 * s, 32 * s, 0.6 * gf)
    }

    // Fade overlay
    if (gf < 1) {
      ctx.fillStyle = NeonColors.bgDark
      ctx.globalAlpha = 1 - gf
      ctx.fillRect(0, 0, w, h)
      ctx.globalAlpha = 1
    }
  }

  /** 60 radial ticks — same proportions as in-game Minimap.ts drawTickRing(). */
  private drawTickRing(
    ctx: CanvasRenderingContext2D,
    cx: number, cz: number, mapR: number,
    t: number, phase: string,
  ): void {
    // Scale from game base: size=168 → half=84, gap=2, tickLen=10, tickW=2
    const ratio = mapR / 84
    const tickLen = 10 * ratio
    const tickW = 2 * ratio
    const innerR = mapR + 2 * ratio
    const outerR = innerR + tickLen

    const isTickPhase = phase === 'round' || phase === 'remaining' || phase === 'current' || phase === 'lastmin'
    // 20 minutes elapsed → 40 remaining (for remaining/current)
    const burnedCount = phase === 'lastmin' ? 59 : 20
    const activeColor = phase === 'lastmin' ? NeonColors.danger : '#DDDDEE'
    const burnedColor = '#444455'

    for (let i = 0; i < TICK_COUNT; i++) {
      const angle = (i / TICK_COUNT) * Math.PI * 2 - Math.PI / 2
      const cosA = Math.cos(angle)
      const sinA = Math.sin(angle)
      const x1 = cx + cosA * innerR
      const z1 = cz + sinA * innerR
      const x2 = cx + cosA * outerR
      const z2 = cz + sinA * outerR

      if (!isTickPhase) {
        // Dim ticks always visible
        ctx.beginPath(); ctx.moveTo(x1, z1); ctx.lineTo(x2, z2)
        ctx.strokeStyle = '#DDDDEE'; ctx.globalAlpha = 0.15
        ctx.lineWidth = tickW; ctx.stroke()
        continue
      }

      if (phase === 'round') {
        // All 60 ticks pulse
        const pulseFade = 0.5 + Math.sin(t * 4 + i * 0.2) * 0.4
        ctx.beginPath(); ctx.moveTo(x1, z1); ctx.lineTo(x2, z2)
        ctx.strokeStyle = activeColor; ctx.globalAlpha = pulseFade
        ctx.lineWidth = tickW; ctx.stroke()
      } else if (phase === 'remaining') {
        // Burned dim, future bright
        ctx.beginPath(); ctx.moveTo(x1, z1); ctx.lineTo(x2, z2)
        if (i < burnedCount) {
          ctx.strokeStyle = burnedColor; ctx.globalAlpha = 0.3
        } else {
          const pulseFade = 0.6 + Math.sin(t * 3 + i * 0.15) * 0.35
          ctx.strokeStyle = activeColor; ctx.globalAlpha = pulseFade
        }
        ctx.lineWidth = tickW; ctx.stroke()
      } else if (phase === 'current') {
        // Only burn point highlighted, rest steady
        if (i < burnedCount) {
          ctx.beginPath(); ctx.moveTo(x1, z1); ctx.lineTo(x2, z2)
          ctx.strokeStyle = burnedColor; ctx.globalAlpha = 0.3
          ctx.lineWidth = tickW; ctx.stroke()
        } else if (i === burnedCount) {
          // Current tick at 30% burn
          const burnFrac = 0.3
          const midX = x2 + (x1 - x2) * burnFrac
          const midZ = z2 + (z1 - z2) * burnFrac

          // Burned part
          ctx.beginPath(); ctx.moveTo(x2, z2); ctx.lineTo(midX, midZ)
          ctx.strokeStyle = burnedColor; ctx.globalAlpha = 0.3
          ctx.lineWidth = tickW; ctx.stroke()

          // Unburned part
          ctx.beginPath(); ctx.moveTo(midX, midZ); ctx.lineTo(x1, z1)
          ctx.strokeStyle = activeColor; ctx.globalAlpha = 0.9
          ctx.lineWidth = tickW; ctx.stroke()

          // Burn point — white blinking with glow
          const blink = Math.abs(Math.sin(Date.now() * 0.00314))
          const perpLen = tickW / 2
          const perpX = -sinA * perpLen
          const perpZ = cosA * perpLen
          ctx.beginPath()
          ctx.moveTo(midX - perpX, midZ - perpZ)
          ctx.lineTo(midX + perpX, midZ + perpZ)
          ctx.strokeStyle = '#FFFFFF'
          ctx.globalAlpha = 0.5 + blink * 0.5
          ctx.lineWidth = tickW
          ctx.shadowColor = '#FFFFFF'; ctx.shadowBlur = 4
          ctx.stroke(); ctx.shadowBlur = 0
        } else {
          // Future — steady dim
          ctx.beginPath(); ctx.moveTo(x1, z1); ctx.lineTo(x2, z2)
          ctx.strokeStyle = activeColor; ctx.globalAlpha = 0.4
          ctx.lineWidth = tickW; ctx.stroke()
        }
      } else {
        // 'lastmin' — 59 burned, 1 red tick pulsing
        if (i < burnedCount) {
          ctx.beginPath(); ctx.moveTo(x1, z1); ctx.lineTo(x2, z2)
          ctx.strokeStyle = burnedColor; ctx.globalAlpha = 0.3
          ctx.lineWidth = tickW; ctx.stroke()
        } else {
          // Last tick — red pulsing with glow
          const dangerPulse = 0.4 + Math.sin(Date.now() * 0.008) * 0.6
          ctx.beginPath(); ctx.moveTo(x1, z1); ctx.lineTo(x2, z2)
          ctx.strokeStyle = NeonColors.danger
          ctx.globalAlpha = dangerPulse
          ctx.lineWidth = tickW
          ctx.shadowColor = NeonColors.danger; ctx.shadowBlur = 6
          ctx.stroke(); ctx.shadowBlur = 0
        }
      }
    }
    ctx.globalAlpha = 1
  }
}
